/**
 * "My List" — on-device shortlist of favorited school IDs with a user-defined
 * 1-based rank. Privacy-first, localStorage only. Mirrors `recentSearches`.
 */

export interface MyListItem {
  schoolId: number;
  addedAt: number; // ms epoch
  rank: number; // 1-based, contiguous
}

interface MyListState {
  version: 1;
  items: MyListItem[];
}

const STORAGE_KEY = 'finderMyList';
export const MY_LIST_CHANGE_EVENT = 'finderMyList:changed';

const emitChange = (): void => {
  try {
    window.dispatchEvent(new Event(MY_LIST_CHANGE_EVENT));
  } catch {
    // no-op outside the browser
  }
};

const isRecord = (value: unknown): value is Record<string, unknown> => Boolean(value) && typeof value === 'object';

/** Coerce a stored entry; drop bad ids. */
const reviveOne = (raw: unknown): MyListItem | null => {
  if (!isRecord(raw)) return null;
  const id = Number(raw.schoolId);
  if (!Number.isFinite(id)) return null;
  const addedAt = Number(raw.addedAt);
  const rank = Number(raw.rank);
  return {
    schoolId: id,
    addedAt: Number.isFinite(addedAt) ? addedAt : Date.now(),
    rank: Number.isFinite(rank) ? rank : 0,
  };
};

const read = (): MyListState => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { version: 1, items: [] };
    const parsed = JSON.parse(raw);
    if (!isRecord(parsed) || !Array.isArray(parsed.items)) return { version: 1, items: [] };
    // Dedupe, sort by stored rank, renumber.
    const seen = new Set<number>();
    const items = parsed.items
      .map(reviveOne)
      .filter((it): it is MyListItem => it !== null)
      .filter((it) => (seen.has(it.schoolId) ? false : seen.add(it.schoolId) && true))
      .sort((a, b) => a.rank - b.rank)
      .map((it, i) => ({ ...it, rank: i + 1 }));
    return { version: 1, items };
  } catch {
    return { version: 1, items: [] };
  }
};

const write = (items: MyListItem[]): void => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: 1, items }));
    emitChange();
  } catch {
    // best effort
  }
};

/** Ranked order, top first. */
export const listMyList = (): MyListItem[] => read().items;

export const isFavorited = (schoolId: number): boolean => read().items.some((i) => i.schoolId === schoolId);

export const addToMyList = (schoolId: number): void => {
  const { items } = read();
  if (items.some((i) => i.schoolId === schoolId)) return;
  write([...items, { schoolId, addedAt: Date.now(), rank: items.length + 1 }]);
};

export const removeFromMyList = (schoolId: number): void => {
  const { items } = read();
  const next = items
    .filter((i) => i.schoolId !== schoolId)
    .map((i, idx) => ({ ...i, rank: idx + 1 }));
  write(next);
};

/** Toggle; returns resulting saved state. */
export const toggleFavorite = (schoolId: number): boolean => {
  const alreadySaved = isFavorited(schoolId);
  if (alreadySaved) removeFromMyList(schoolId);
  else addToMyList(schoolId);
  return !alreadySaved;
};

/** Reorder by new id order; unknown ids ignored. */
export const reorderMyList = (orderedIds: number[]): void => {
  const { items } = read();
  const byId = new Map(items.map((i) => [i.schoolId, i]));
  const next: MyListItem[] = [];
  orderedIds.forEach((id) => {
    const it = byId.get(id);
    if (it && !next.some((n) => n.schoolId === id)) {
      next.push({ ...it, rank: next.length + 1 });
    }
  });
  // Omitted ids keep their order at the end.
  items.forEach((it) => {
    if (!next.some((n) => n.schoolId === it.schoolId)) {
      next.push({ ...it, rank: next.length + 1 });
    }
  });
  write(next);
};

export const clearMyList = (): void => {
  write([]);
};
